'use client';

import { formatScore } from './score-format-policy';
import { TScoreSkillLevel, TNormalizedRatingCutoffs } from '@/types';
import {
  APP_SCORING_FORMAT,
  APP_SCORING_MAX_SCORE,
  APP_SCORING_PERCENTAGE_RANGES,
  FEATURE_FLAGS,
  SKILL_LEVELS,
} from '@/constants';
import {
  RESOLVED_SKILL_LEVEL_LABELS,
  SKILL_LEVEL_BY_CODE,
  SKILL_LEVEL_DEFINITIONS,
  TSkillLevelId,
} from '@/constants/score';

// ================================
// Types
// ================================

/**
 * Local convenience alias for a skill level definition entry.
 *
 * We derive this directly from the authoritative `SKILL_LEVEL_DEFINITIONS`
 * object to avoid any drift between policy typing and constants typing.
 */
type TSkillLevelDefinition = (typeof SKILL_LEVEL_DEFINITIONS)[TSkillLevelId];

// ================================
// Internal Constants
// ================================

/**
 * Canonical skill level codes accepted by the app.
 *
 * Important:
 * - This list intentionally excludes legacy aliases (`beginner`, `novice`,
 *   `intermediate`) because normalization first checks canonical values and
 *   then falls back to alias resolution.
 * - Keep this list aligned with `SKILL_LEVEL_DEFINITIONS` L1-L4 codes.
 */
const CANONICAL_SKILL_LEVELS = [
  SKILL_LEVELS.AWARENESS,
  SKILL_LEVELS.SKILLED,
  SKILL_LEVELS.ADVANCED,
  SKILL_LEVELS.EXPERT,
] as const;

// ================================
// Internal Helpers
// ================================

/**
 * Returns the versioned skill level code for a given canonical code.
 *
 * - v2 enabled: returns the canonical code unchanged.
 * - v2 disabled: returns the first legacy alias if one exists, otherwise the
 *   canonical code (L3/L4 have no legacy aliases and are returned as-is).
 */
const toVersionedCode = (canonicalCode: TScoreSkillLevel): TScoreSkillLevel => {
  if (FEATURE_FLAGS.ENABLE_SCORECARD_V2) return canonicalCode;
  const id = SKILL_LEVEL_BY_CODE[canonicalCode];
  const primaryAlias = SKILL_LEVEL_DEFINITIONS[id]?.legacyAliases[0];
  return (primaryAlias as TScoreSkillLevel | undefined) ?? canonicalCode;
};

/**
 * Maps normalized numeric score buckets (1..4) to canonical skill level codes.
 *
 * Behavior contract:
 * - `4` => `expert`
 * - `3` => `advanced`
 * - `2` => `skilled`
 * - any other value (including `1`, decimals, NaN) => `awareness`
 */
const mapNumericScoreToSkillLevel = (value: number): TScoreSkillLevel => {
  switch (value) {
    case 4:
      return toVersionedCode(SKILL_LEVELS.EXPERT);
    case 3:
      return toVersionedCode(SKILL_LEVELS.ADVANCED);
    case 2:
      return toVersionedCode(SKILL_LEVELS.SKILLED);
    case 1:
    default:
      return toVersionedCode(SKILL_LEVELS.AWARENESS);
  }
};

// ================================
// Public Label/Definition Helpers
// ================================

/**
 * Resolves a user-facing label for a skill level code.
 *
 * Supports both canonical codes and legacy aliases via
 * `RESOLVED_SKILL_LEVEL_LABELS`.
 */
export function getSkillLevelLabel(skillLevelCode: TScoreSkillLevel): string {
  return RESOLVED_SKILL_LEVEL_LABELS[skillLevelCode];
}

/**
 * Returns the immutable skill level definition for a given stable ID.
 *
 * Stable IDs (`L1`..`L4`) protect code paths from terminology shifts by
 * decoupling internal identifiers from display wording.
 */
export function getSkillLevelDefinition(id: TSkillLevelId): TSkillLevelDefinition {
  return SKILL_LEVEL_DEFINITIONS[id];
}

/**
 * Returns a skill level definition from a skill code (canonical or alias).
 *
 * Flow:
 * 1) Resolve code -> stable ID using `SKILL_LEVEL_BY_CODE`.
 * 2) Resolve stable ID -> definition from `SKILL_LEVEL_DEFINITIONS`.
 */
export function getSkillLevelDefinitionByCode(
  skillLevelCode: TScoreSkillLevel,
): TSkillLevelDefinition {
  const id = SKILL_LEVEL_BY_CODE[skillLevelCode];
  return SKILL_LEVEL_DEFINITIONS[id];
}

/**
 * Normalizes freeform skill input to canonical skill level codes.
 *
 * Supported inputs:
 * - Canonical codes: `awareness`, `skilled`, `advanced`, `expert`
 * - Legacy aliases: `beginner`, `novice`, `intermediate`
 * - Case-insensitive input with whitespace trimming
 *
 * @returns Canonical skill level code, or `null` if input is missing/unknown.
 */
export function normalizeSkillLevel(skillLevel?: string | null): TScoreSkillLevel | null {
  // Guard missing/empty input early.
  if (!skillLevel) {
    return null;
  }

  // Compare normalized lowercase values only.
  const normalizedInput = skillLevel.trim().toLowerCase();

  // Fast-path for canonical values.
  if (CANONICAL_SKILL_LEVELS.includes(normalizedInput as (typeof CANONICAL_SKILL_LEVELS)[number])) {
    return toVersionedCode(normalizedInput as TScoreSkillLevel);
  }

  // Fallback: resolve known legacy aliases from definitions registry.
  for (const definition of Object.values(SKILL_LEVEL_DEFINITIONS)) {
    if ((definition.legacyAliases as readonly string[]).includes(normalizedInput)) {
      return toVersionedCode(definition.code);
    }
  }

  // Unknown term.
  return null;
}

/**
 * Maps a numeric simulation score into a canonical skill level code.
 *
 * This policy keeps score-to-level behavior centralized and format-aware.
 * The same API can handle different app score formats (`numerical grade`,
 * `percentage`, and other numerical variants) by delegating normalization to
 * `formatScore(...)` first.
 *
 * When `runtimeRatingCutoffs` is provided (from backend-supplied percentage
 * bands), it overrides the static `APP_SCORING_PERCENTAGE_RANGES` for
 * percentage-based scoring. This allows dynamic L1–L4 thresholds per score.
 *
 * Decision rules:
 * 1) `null`/`undefined` score => `null` (unknown).
 * 2) `0` => `beginner` alias (intentionally preserved behavior).
 * 3) `numerical grade` => parse the grade segment (before `/`).
 * 4) `percentage` => map thresholds using runtime ranges (if provided) or
 *    static `APP_SCORING_PERCENTAGE_RANGES` (fallback).
 * 5) default numerical formats => parse numeric output and bucket via 1..4 map.
 */
export function getSkillLevel(
  score?: number | null,
  runtimeRatingCutoffs?: TNormalizedRatingCutoffs,
): TScoreSkillLevel | null {
  // Missing score means we cannot infer a level.
  if (score == null) {
    return null;
  }

  // Preserve historical semantics where explicit zero maps to level-1 alias.
  if (score === 0) {
    return toVersionedCode(SKILL_LEVELS.BEGINNER);
  }

  // Normalize the incoming score according to configured app format first.
  const convertedScoreString = formatScore(score, APP_SCORING_MAX_SCORE, APP_SCORING_FORMAT);

  // Use runtime ranges if provided, otherwise fall back to static constants.
  const percentageRanges = runtimeRatingCutoffs || APP_SCORING_PERCENTAGE_RANGES;

  switch (APP_SCORING_FORMAT) {
    case 'numerical grade': {
      // Example grade format: "3/4" -> extract "3".
      const numericalGrade = parseFloat(convertedScoreString.split('/')[0]);
      return mapNumericScoreToSkillLevel(numericalGrade);
    }

    case 'percentage': {
      // Example percentage format: "89%" -> extract 89.
      const percentageValue = parseInt(convertedScoreString.replace('%', ''), 10);
      if (percentageValue >= percentageRanges.L4[0]) {
        return toVersionedCode(SKILL_LEVELS.EXPERT);
      } else if (percentageValue >= percentageRanges.L3[0]) {
        return toVersionedCode(SKILL_LEVELS.ADVANCED);
      } else if (percentageValue >= percentageRanges.L2[0]) {
        return toVersionedCode(SKILL_LEVELS.SKILLED);
      }

      return toVersionedCode(SKILL_LEVELS.AWARENESS);
    }

    default: {
      // Default numerical output can be decimals; bucketization handles fallback.
      const defaultedScore = parseFloat(convertedScoreString);
      return mapNumericScoreToSkillLevel(defaultedScore);
    }
  }
}